import { Star, Quote, ExternalLink, PenLine } from "lucide-react";
import { REVIEWS, GOOGLE } from "@/data";

const ReviewCard = ({ r }) => (
  <div className="w-[340px] shrink-0 rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
    <Quote className="text-brand/30" size={32} strokeWidth={1.5} />
    <div className="mt-3 flex text-brand">
      {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}
    </div>
    <p className="mt-4 text-slate-700 leading-relaxed">"{r.text}"</p>
    <div className="mt-5 flex items-center gap-3">
      <div className="grid place-items-center h-10 w-10 rounded-full bg-ink text-white font-display font-bold">
        {r.name.charAt(0)}
      </div>
      <div>
        <p className="font-semibold text-slate-900 text-sm">{r.name}</p>
        <p className="text-xs text-slate-500">{r.city}, CO</p>
      </div>
    </div>
  </div>
);

const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

export const Reviews = () => {
  const doubled = [...REVIEWS, ...REVIEWS];
  return (
    <section id="reviews" className="py-24 bg-[#F8FAFC] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <p className="uppercase tracking-[0.2em] text-sm font-semibold text-brand">Reviews</p>
          <h2 className="mt-3 font-display font-bold text-4xl md:text-5xl tracking-tight text-slate-900">
            Loved by local homeowners
          </h2>
        </div>

        {/* Google rating card */}
        <div
          data-testid="google-rating-card"
          className="mt-10 mx-auto max-w-3xl rounded-2xl bg-white border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 flex flex-col sm:flex-row items-center gap-8"
        >
          <div className="flex flex-col items-center text-center shrink-0">
            <div className="flex items-center gap-2 text-slate-500 text-sm font-medium">
              <GoogleG /> Google Reviews
            </div>
            <p className="mt-3 font-display font-extrabold text-6xl text-slate-900 leading-none">
              {GOOGLE.rating.toFixed(1)}
            </p>
            <div className="mt-2 flex text-amber-400">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="currentColor" strokeWidth={0} />)}
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Based on <span className="font-semibold text-slate-700">{GOOGLE.count}</span> reviews
            </p>
          </div>

          <div className="hidden sm:block w-px self-stretch bg-slate-200" />

          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-display font-semibold text-2xl text-slate-900">
              Rated 5 stars by your neighbors
            </h3>
            <p className="mt-2 text-slate-600 leading-relaxed">
              Don't just take our word for it — read what {GOOGLE.count}+ Castle Rock, Larkspur & Parker customers have to say on Google.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <a
                href={GOOGLE.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="read-google-reviews-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-ink hover:bg-slate-800 text-white font-semibold px-6 py-3 transition-all duration-300 hover:-translate-y-0.5"
              >
                Read all {GOOGLE.count} reviews <ExternalLink size={16} strokeWidth={2} />
              </a>
              <a
                href={GOOGLE.writeReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leave-google-review-button"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white hover:border-brand hover:text-brand text-slate-800 font-semibold px-6 py-3 transition-all duration-300"
              >
                <PenLine size={16} strokeWidth={2} /> Leave a review
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-14 relative" data-testid="reviews-marquee">
        <div className="flex gap-6 w-max animate-marquee hover:[animation-play-state:paused]">
          {doubled.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#F8FAFC] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#F8FAFC] to-transparent" />
      </div>
    </section>
  );
};
